/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useCallback } from 'react';

interface StartupProgressContextType {
    progress: number;
    status: string;
    updateProgress: (value: number, status?: string) => void;
    isComplete: boolean;
    complete: () => void;
}

// #comment: Global context to coordinate the application's multi-stage initialization.
// It centralizes progress reporting from different service layers (Config, User, SDK)
// and ensures a unified, smooth startup animation that masks underlying API latencies.
const StartupProgressContext = createContext<StartupProgressContextType | undefined>(undefined);

export const StartupProgressProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [progress, setProgress] = useState(10);
    const [status, setStatus] = useState('Starting P2P Hub...');
    const [isComplete, setIsComplete] = useState(false);

    const updateProgress = useCallback((value: number, newStatus?: string) => {
        setProgress(prev => Math.max(prev, value));
        if (newStatus) setStatus(newStatus);
    }, []);

    const complete = useCallback(() => {
        setProgress(100);
        setStatus('Ready!');
        // Brief moment so user sees 100% tick, then immediately reveal the app
        setTimeout(() => setIsComplete(true), 300);
    }, []);

    const contextValue = React.useMemo(() => ({
        progress,
        status,
        updateProgress,
        isComplete,
        complete
    }), [progress, status, updateProgress, isComplete, complete]);

    return (
        <StartupProgressContext.Provider value={contextValue}>
            {children}
        </StartupProgressContext.Provider>
    );
};

export const useStartupProgress = () => {
    const context = useContext(StartupProgressContext);
    if (!context) {
        // Fallback for components outside provider
        return {
            progress: 100,
            status: '',
            updateProgress: () => { },
            isComplete: true,
            complete: () => { }
        };
    }
    return context;
};
